/** Finanzas Current/Previous income tables — FE heuristics (no IncomeType from API). */

const INCOME_LIST_HEADERS = new Set(["Current", "Previous"]);

const CURRENCY_DISPLAY_PATTERN = /^\$?[\d,]+(\.\d{2})?$/;
/** Matches backend FormatHelper employment date ranges (slash or dash). */
const DATE_RANGE_DISPLAY_PATTERN =
  /\d{1,2}[/-]\d{1,2}[/-]\d{2,4}/;
const PRESENT_TOKEN = "Present";

export const isIncomeListSection = (header: string | undefined | null): boolean =>
  Boolean(header && INCOME_LIST_HEADERS.has(header.trim()));

export const isCurrencyCell = (value: string | null | undefined): boolean => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    return false;
  }
  return CURRENCY_DISPLAY_PATTERN.test(trimmed);
};

export const isDateRangeCell = (value: string | null | undefined): boolean => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    return false;
  }
  return DATE_RANGE_DISPLAY_PATTERN.test(trimmed) || trimmed.includes(PRESENT_TOKEN);
};

export const rowHasDateRange = (columns: string[]): boolean =>
  columns.some((column) => isDateRangeCell(column));

/**
 * Employment-like rows include dates → protect typed name.
 * Other-income rows usually have empty date cell → allow name translate.
 */
export const shouldProtectIncomeName = (columns: string[]): boolean =>
  rowHasDateRange(columns);

export const shouldProtectIncomeAmount = (value: string | null | undefined): boolean =>
  isCurrencyCell(value) || Boolean(value?.trim());

/** Split date cell so numeric dates stay NoTx and "Present" can translate. */
export type DateCellPart =
  | { kind: "protected"; text: string }
  | { kind: "translatable"; text: string };

export const splitDateCellForTranslation = (value: string | null | undefined): DateCellPart[] => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    return [];
  }

  const presentIndex = trimmed.indexOf(PRESENT_TOKEN);
  if (presentIndex < 0) {
    return [{ kind: "protected", text: trimmed }];
  }

  const before = trimmed.slice(0, presentIndex);
  const present = trimmed.slice(presentIndex, presentIndex + PRESENT_TOKEN.length);
  const after = trimmed.slice(presentIndex + PRESENT_TOKEN.length);

  const parts: DateCellPart[] = [];
  if (before) {
    parts.push({ kind: "protected", text: before });
  }
  parts.push({ kind: "translatable", text: present });
  if (after) {
    parts.push({ kind: "protected", text: after });
  }
  return parts;
};
